<template>
    <ExerciseForm v-if="editing"
                  v-model="form.fields"
                  @cancel="handleExerciseCancel"
                  @delete="handleExerciseDelete"
                  @save="handleExerciseSave"
    />

    <div class="flex items-center justify-between mb-1">
        <Label class="!mb-0">Workout Items</Label>
        <div class="border rounded px-2 text-sm cursor-pointer" @click="$emit('toggle')" v-if="localItems.length > 1">
            {{ sorting ? 'Done Sorting' : 'Sort Exercises' }}
        </div>
    </div>

    <div class="mb-3">
        <draggable v-model="localItems"
                   @start="drag=true"
                   @end="drag=false"
                   item-key="key"
                   handle=".drag-handle"
                   chosen-class="chosen"
        >
            <template #item="{element, index}">
                <div v-if="element.type === 'exercise'" :class="exerciseSetsEnabled ? exerciseItemClasses(index) : null">
                    <template v-if="exerciseSetsEnabled">
                        <div class="flex items-center justify-between gap-3 border-b border-neutral-200 px-3 py-2 dark:border-neutral-700"
                             data-cy="exercise-set-group"
                             v-if="['single', 'first', 'only'].includes(setGroupPosition(index))">
                            <div class="min-w-0">
                                <div class="truncate font-semibold">{{ movementName(element.exercise) }}</div>
                            </div>
                            <button type="button"
                                    class="flex shrink-0 items-center justify-center gap-1 rounded border border-neutral-500 px-3 py-2 text-sm"
                                    data-cy="add-set"
                                    v-if="!sorting && element.exercise.movement"
                                    @click="handleSetAdd(setGroupLastItemKey(element.exercise) || element.key)">
                                <Icon fixedWidth icon="plus"/>
                                Set
                            </button>
                        </div>
                        <div class="flex items-stretch bg-neutral-50 dark:bg-white/[2%]">
                            <div class="flex w-16 shrink-0 items-center justify-center border-r border-neutral-200 text-xs font-semibold text-neutral-600 dark:border-neutral-700 dark:text-neutral-400"
                                 data-cy="exercise-set-label">
                                Set {{ setNumber(element.exercise) }}
                            </div>
                            <Row class="min-w-0 flex-1 cursor-pointer"
                                 :exercise="element.exercise"
                                 :lastExercise="lastExerciseFor(element.exercise)"
                                 @click="handleExerciseEdit(element.exercise)"
                                 :sortable="sorting"
                                 :bordered="false"
                                 :compact="true"
                                 :removable="true"
                                 :show-movement-name="false"
                                 @delete="handleExerciseRemove(element.key)"
                            />
                        </div>
                    </template>
                    <Row v-else
                         class="cursor-pointer"
                         :exercise="element.exercise"
                         :lastExercise="lastExerciseFor(element.exercise)"
                         @click="handleExerciseEdit(element.exercise)"
                         :sortable="sorting"
                         :bordered="true"
                         :compact="false"
                         :show-movement-name="true"
                    />
                </div>
                <RestTimerBlock
                    v-else
                    :countdown-sound-enabled="restTimerCountdownSound"
                    :duration="Number(element.restTimer.duration || restTimerDuration || 90)"
                    :label="element.restTimer.label"
                    :remaining-value="element.restTimer.remaining ?? null"
                    :removable="element.restTimer.source === 'custom'"
                    :rank="element.restTimer.rank"
                    :source="element.restTimer.source || 'custom'"
                    :sortable="sorting"
                    :status="element.restTimer.status || 'ready'"
                    :template-id="templateId"
                    :template-item-rank="element.restTimer.templateItemRank"
                    @change="handleRestTimerChange(element.key, $event)"
                    @remove="handleRestTimerRemove(element.key)"
                />
            </template>
        </draggable>
    </div>

    <div class="grid grid-cols-1 gap-3">
        <Button block primary outlined @click="handleExerciseAdd">
            Add Exercise
        </Button>
        <Button v-if="restTimerEnabled" block secondary outlined @click="handleRestTimerAdd">
            <Icon fixedWidth icon="stopwatch"/>
            Add Rest Timer
        </Button>
    </div>
</template>

<script>
import {defineComponent} from 'vue'
import {mapActions} from 'vuex';
import Row from '@/components/Exercise/Row.vue';
import RestTimerBlock from '@/components/RestTimerBlock.vue';
import ExerciseForm from '@/components/Exercise/Form.vue';
import {Exercise, Form} from '@/internal.js';
import {uuid} from '@/helpers.js';
import draggable from 'vuedraggable'
import {capturePostHogEvent} from '@/utils/posthog';

const defaultExercise = {
  advanced: false,
  bandIds: [],
  duration: 0,
  exhaustionLevel: null,
  movementId: null,
  notes: null,
  partialRepetitions: null,
  rank: null,
  repetitions: null,
  setId: null,
  weight: null,
};

export default defineComponent({
  name: 'ExercisesInput',
  components: {
    draggable,
    ExerciseForm,
    RestTimerBlock,
    Row,
  },
  emits: [
    'update:modelValue',
    'update:restTimers',
    'toggle',
  ],
  data() {
    return {
      drag: false,
      editing: false,
      form: new Form({
        ...defaultExercise,
      }),
      lastExerciseIds: {},
      localItems: [],
      pendingExerciseInsertAfterSet: null,
      pendingExerciseInsertAfterKey: null,
      syncing: false,
    };
  },
  computed: {
    exerciseItems() {
      return this.localItems
        .filter(item => item.type === 'exercise')
        .map(item => item.exercise);
    },
    lastExercisesKey() {
      return this.exerciseItems
        .map((exercise, index) => [
          this.exerciseKey(exercise, index),
          exercise.id || '',
          exercise.movementId || exercise.movement?.id || '',
          exercise.rank || '',
          exercise.workoutId || '',
        ].join(':'))
        .join('|');
    },
    nextRank() {
      return this.localItems.length + 1;
    },
  },
  methods: {
    ...mapActions({
      exerciseLast: 'exercise/last',
    }),
    rankLocalItems(items) {
      return items.map((item, index) => {
        const rank = index + 1;

        if (item.type === 'exercise') {
          return {
            ...item,
            rank,
            exercise: new Exercise({
              ...item.exercise,
              rank,
            }),
          };
        }

        return {
          ...item,
          rank,
          restTimer: {
            ...item.restTimer,
            rank,
          },
        };
      });
    },
    buildLocalItems(exercises, restTimers) {
      const exerciseItems = (exercises || []).map((exercise, index) => {
        const rank = Number(exercise.rank || index + 1);
        const exerciseModel = exercise instanceof Exercise ? exercise : new Exercise(exercise);
        exerciseModel.rank = rank;

        return {
          key: `exercise-${this.exerciseKey(exercise, index)}`,
          type: 'exercise',
          rank,
          exercise: exerciseModel,
        };
      });

      const restTimerItems = (restTimers || []).map((timer, index) => {
        const key = timer.key || uuid();
        const rank = Number(timer.rank || timer.templateItemRank || timer.template_item_rank || index + 1);

        return {
          key,
          type: 'rest_timer',
          rank,
          restTimer: {
            key,
            label: timer.label || 'Rest Timer',
            duration: Number(timer.duration || 90),
            rank,
            remaining: Number(timer.remaining ?? timer.duration ?? 90),
            source: timer.source || 'custom',
            status: timer.status || 'ready',
            templateItemRank: timer.templateItemRank || timer.template_item_rank || null,
          },
        };
      });

      return [
        ...exerciseItems,
        ...restTimerItems,
      ].sort((a, b) => a.rank > b.rank ? 1 : -1);
    },
    emitItems() {
      const exercises = [];
      const restTimers = [];

      this.localItems.forEach((item, index) => {
        const rank = index + 1;

        if (item.type === 'exercise') {
          exercises.push(new Exercise({
            ...item.exercise,
            rank,
          }));
          return;
        }

        restTimers.push({
          ...item.restTimer,
          key: item.key,
          rank,
        });
      });

      if (JSON.stringify(exercises) !== JSON.stringify(this.modelValue)) {
        this.$emit('update:modelValue', exercises);
      }

      if (JSON.stringify(restTimers) !== JSON.stringify(this.restTimers)) {
        this.$emit('update:restTimers', restTimers);
      }
    },
    exerciseKey(exercise, index = null) {
      return exercise.id || [
        exercise.movementId || exercise.movement?.id || 'movement',
        exercise.rank || 'rank',
        index ?? this.exerciseItems.indexOf(exercise),
      ].join('-');
    },
    handleExerciseAdd() {
      this.pendingExerciseInsertAfterSet = null;
      this.pendingExerciseInsertAfterKey = null;
      this.form.fields = {
        ...defaultExercise,
      };
      this.editing = true;
    },
    handleExerciseCancel() {
      this.pendingExerciseInsertAfterSet = null;
      this.pendingExerciseInsertAfterKey = null;
      this.form.fields = {};
      this.editing = false;
    },
    handleExerciseDelete() {
      this.pendingExerciseInsertAfterSet = null;
      this.pendingExerciseInsertAfterKey = null;
      this.localItems = this.rankLocalItems(this.localItems.filter((item) => {
        if (item.type !== 'exercise') {
          return true;
        }

        if (item.exercise.id === this.form.fields.id) {
          return false;
        }

        return true;
      }));
      this.editing = false;
    },
    handleExerciseRemove(key) {
      const item = this.localItems.find((item) => item.key === key && item.type === 'exercise');

      this.pendingExerciseInsertAfterSet = null;
      this.pendingExerciseInsertAfterKey = null;
      this.localItems = this.rankLocalItems(this.localItems.filter((item) => item.key !== key));

      if (item && this.setGroupKey(item.exercise)) {
        capturePostHogEvent('workout_exercise_set_removed', {
          movement_id: item.exercise.movementId || item.exercise.movement?.id || null,
          set_count_before_remove: this.setCount(item.exercise),
        });
      }
    },
    handleExerciseEdit(exercise) {

      if (this.sorting) {
        return;
      }

      this.form.fields = {
        ...defaultExercise,
        ...exercise,
      };
      this.editing = true;
    },
    handleExerciseSave() {
      let savedExerciseKey = null;

      if (this.form.fields.id) {
        this.localItems = this.rankLocalItems([
          ...this.localItems.map((item) => {
            if (item.type === 'exercise' && item.exercise.id === this.form.fields.id) {
              savedExerciseKey = item.key;

              return {
                ...item,
                exercise: new Exercise({
                  ...this.form.fields,
                  rank: item.rank,
                }),
              };
            }

            return item;
          }),
        ]);
      } else {
        const id = uuid();
        savedExerciseKey = `exercise-${id}`;
        const newExerciseItem = {
          key: `exercise-${id}`,
          type: 'exercise',
          rank: this.nextRank,
          exercise: new Exercise({
            ...this.form.fields,
            id,
            rank: this.nextRank,
          }),
        };

        if (this.pendingExerciseInsertAfterKey) {
          const insertAfterIndex = this.pendingExerciseInsertAfterIndex();

          if (insertAfterIndex >= 0) {
            this.localItems = this.rankLocalItems([
              ...this.localItems.slice(0, insertAfterIndex + 1),
              newExerciseItem,
              ...this.localItems.slice(insertAfterIndex + 1),
            ]);
          } else {
            this.localItems = this.rankLocalItems([
              ...this.localItems,
              newExerciseItem,
            ]);
          }
        } else {
          this.localItems = this.rankLocalItems([
            ...this.localItems,
            newExerciseItem,
          ]);
        }
      }

      this.handleFollowingRestTimerAutoStart(savedExerciseKey);
      this.pendingExerciseInsertAfterSet = null;
      this.pendingExerciseInsertAfterKey = null;
      this.editing = false;
      this.form.fields = {};
    },
    handleSetAdd(key) {
      if (!this.exerciseSetsEnabled) {
        return;
      }

      const item = this.localItems.find(item => item.key === key && item.type === 'exercise');

      if (!item) {
        return;
      }

      const setId = item.exercise.setId || item.exercise.set_id || uuid();

      this.localItems = this.localItems.map((localItem) => {
        if (localItem.key !== key || localItem.type !== 'exercise') {
          return localItem;
        }

        return {
          ...localItem,
          exercise: new Exercise({
            ...localItem.exercise,
            setId,
          }),
        };
      });

      this.pendingExerciseInsertAfterKey = key;
      this.pendingExerciseInsertAfterSet = {
        movementId: item.exercise.movementId || item.exercise.movement?.id || null,
        setId,
      };
      this.form.fields = {
        ...defaultExercise,
        ...item.exercise,
        id: null,
        duration: 0,
        exhaustionLevel: null,
        notes: null,
        partialRepetitions: null,
        repetitions: null,
        setId,
      };
      this.editing = true;

      capturePostHogEvent('workout_exercise_set_added', {
        movement_id: item.exercise.movementId || item.exercise.movement?.id || null,
        set_count_before_add: this.setCount(item.exercise),
      });
    },
    pendingExerciseInsertAfterIndex() {
      const keyIndex = this.localItems.findIndex(item => item.key === this.pendingExerciseInsertAfterKey);

      if (keyIndex >= 0) {
        return keyIndex;
      }

      if (!this.pendingExerciseInsertAfterSet?.setId) {
        return -1;
      }

      const groupKey = `set:${this.pendingExerciseInsertAfterSet.setId}`;

      return this.localItems
        .map((item, index) => ({item, index}))
        .filter(({item}) => {
          if (item.type !== 'exercise' || this.setGroupKey(item.exercise) !== groupKey) {
            return false;
          }

          const movementId = item.exercise.movementId || item.exercise.movement?.id || null;

          return !this.pendingExerciseInsertAfterSet.movementId || movementId === this.pendingExerciseInsertAfterSet.movementId;
        })
        .map(({index}) => index)
        .pop() ?? -1;
    },
    handleRestTimerAdd() {
      const duration = Number(this.restTimerDuration || 90);
      const key = uuid();

      this.localItems = this.rankLocalItems([
        ...this.localItems,
        {
          key,
          type: 'rest_timer',
          rank: this.nextRank,
          restTimer: {
            key,
            source: 'custom',
            duration,
            label: 'Rest Timer',
            rank: this.nextRank,
            remaining: duration,
            status: 'ready',
          },
        },
      ]);

      capturePostHogEvent('workout_rest_timer_added', {
        duration,
        used_template: !!this.templateId,
      });
    },
    handleRestTimerChange(key, changes) {
      let shouldAutoOpenNextExercise = false;

      this.localItems = this.localItems.map((item) => {
        if (item.key !== key || item.type !== 'rest_timer') {
          return item;
        }

        shouldAutoOpenNextExercise = this.shouldAutoOpenNextExercise(item.restTimer, changes);

        return {
          ...item,
          restTimer: {
            ...item.restTimer,
            ...changes,
          },
        };
      });

      if (shouldAutoOpenNextExercise) {
        this.$nextTick(() => {
          this.handleNextExerciseOpen(key);
        });
      }
    },
    handleRestTimerRemove(key) {
      this.localItems = this.rankLocalItems(this.localItems.filter(item => item.key !== key));

      capturePostHogEvent('workout_rest_timer_removed', {
        used_template: !!this.templateId,
      });
    },
    handleFollowingRestTimerAutoStart(exerciseKey) {
      if (!this.restTimerAutoStartOnExerciseSave || !exerciseKey) {
        return;
      }

      const exerciseIndex = this.localItems.findIndex(item => item.key === exerciseKey);
      const restTimerIndex = exerciseIndex + 1;
      const nextItem = this.localItems[restTimerIndex];

      if (exerciseIndex < 0 || !nextItem || nextItem.type !== 'rest_timer') {
        return;
      }

      if (!this.restTimerCanAutoStart(nextItem.restTimer)) {
        return;
      }

      const duration = Number(nextItem.restTimer.duration || this.restTimerDuration || 90);

      this.localItems = this.localItems.map((item, index) => {
        if (index !== restTimerIndex) {
          return item;
        }

        return {
          ...item,
          restTimer: {
            ...item.restTimer,
            duration,
            remaining: duration,
            status: 'running',
          },
        };
      });

      capturePostHogEvent('workout_rest_timer_auto_started', {
        duration,
        used_template: !!this.templateId,
      });
    },
    handleNextExerciseOpen(restTimerKey) {
      const restTimerIndex = this.localItems.findIndex(item => item.key === restTimerKey);
      const nextItem = this.localItems[restTimerIndex + 1];

      if (restTimerIndex < 0 || !nextItem || nextItem.type !== 'exercise') {
        return;
      }

      this.handleExerciseEdit(nextItem.exercise);

      capturePostHogEvent('workout_rest_timer_next_exercise_opened', {
        used_template: !!this.templateId,
      });
    },
    restTimerCanAutoStart(restTimer) {
      return ['ready', null, undefined].includes(restTimer?.status);
    },
    exerciseItemClasses(index) {
      const position = this.setGroupPosition(index);

      const base = 'overflow-hidden border-neutral-200 bg-white dark:border-neutral-700 dark:bg-white/[2%]';

      if (['single', 'only'].includes(position)) {
        return `${base} my-2 rounded border`;
      }

      if (position === 'first') {
        return `${base} mt-2 rounded-t border-x border-t`;
      }

      if (position === 'last') {
        return `${base} mb-2 rounded-b border-x border-t border-b`;
      }

      return `${base} border-x border-t`;
    },
    movementName(exercise) {
      return exercise.movement ? exercise.movement.name : 'Movement';
    },
    setGroupPosition(index) {
      const item = this.localItems[index];

      if (!item || item.type !== 'exercise') {
        return 'single';
      }

      const key = this.setGroupKey(item.exercise);

      if (!key || this.setCount(item.exercise) < 2) {
        return 'single';
      }

      const previousItem = this.localItems[index - 1];
      const nextItem = this.localItems[index + 1];
      const hasPreviousSet = previousItem?.type === 'exercise' && this.setGroupKey(previousItem.exercise) === key;
      const hasNextSet = nextItem?.type === 'exercise' && this.setGroupKey(nextItem.exercise) === key;

      if (!hasPreviousSet && !hasNextSet) {
        return 'only';
      }

      if (!hasPreviousSet) {
        return 'first';
      }

      if (!hasNextSet) {
        return 'last';
      }

      return 'middle';
    },
    setCount(exercise) {
      const key = this.setGroupKey(exercise);

      if (!key) {
        return 1;
      }

      return this.exerciseItems.filter(item => this.setGroupKey(item) === key).length;
    },
    setIndex(exercise) {
      const key = this.setGroupKey(exercise);

      if (!key) {
        return null;
      }

      return this.exerciseItems.filter(item => this.setGroupKey(item) === key).findIndex(item => item.id === exercise.id) + 1;
    },
    setNumber(exercise) {
      return this.setIndex(exercise) || 1;
    },
    setGroupLastItemKey(exercise) {
      const key = this.setGroupKey(exercise);

      if (!key) {
        return null;
      }

      const item = [...this.localItems].reverse().find(item => {
        return item.type === 'exercise' && this.setGroupKey(item.exercise) === key;
      });

      return item?.key || null;
    },
    setGroupKey(exercise) {
      const setId = exercise.setId || exercise.set_id;

      return setId ? `set:${setId}` : null;
    },
    shouldAutoOpenNextExercise(restTimer, changes) {
      if (!this.restTimerAutoOpenNextExerciseOnComplete || this.sorting || this.editing) {
        return false;
      }

      return restTimer?.status !== 'completed' && changes?.status === 'completed';
    },
    persistedExerciseId(exercise) {
      const id = Number(exercise.id);

      return Number.isInteger(id) && id > 0 ? id : null;
    },
    lastExerciseFor(exercise) {
      const lastExerciseId = this.lastExerciseIds[this.exerciseKey(exercise)];

      return lastExerciseId ? this.$store.getters['exercise/findBy'](lastExerciseId, 'id') : null;
    },
    loadLastExercises() {
      this.exerciseItems.forEach((exercise, index) => {
        const movementId = exercise.movementId || exercise.movement?.id;

        if (!movementId) {
          return;
        }

        const key = this.exerciseKey(exercise, index);

        this.exerciseLast({
          params: {
            exerciseId: this.persistedExerciseId(exercise),
            movementId,
            rank: exercise.rank,
            workoutId: exercise.workoutId,
          },
        }).then((ids) => {
          this.lastExerciseIds = {
            ...this.lastExerciseIds,
            [key]: ids.length ? ids[0] : null,
          };
        }).catch(() => {
          this.lastExerciseIds = {
            ...this.lastExerciseIds,
            [key]: null,
          };
        });
      });
    },
    syncLocalItems() {
      const nextItems = this.buildLocalItems(this.modelValue, this.restTimers);

      if (JSON.stringify(nextItems) === JSON.stringify(this.localItems)) {
        return;
      }

      this.syncing = true;
      this.localItems = nextItems;
      this.$nextTick(() => {
        this.syncing = false;
      });
    },
  },
  props: {
    modelValue: {
      required: true,
      type: Array,
    },
    restTimerDuration: {
      default: 90,
      required: false,
      type: Number,
    },
    restTimerCountdownSound: {
      default: true,
      required: false,
      type: Boolean,
    },
    restTimerAutoStartOnExerciseSave: {
      default: false,
      required: false,
      type: Boolean,
    },
    restTimerAutoOpenNextExerciseOnComplete: {
      default: false,
      required: false,
      type: Boolean,
    },
    restTimerEnabled: {
      default: false,
      required: false,
      type: Boolean,
    },
    exerciseSetsEnabled: {
      default: true,
      required: false,
      type: Boolean,
    },
    restTimers: {
      default: () => [],
      required: false,
      type: Array,
    },
    sorting: {
      required: false,
      type: Boolean,
    },
    templateId: {
      default: null,
      required: false,
    }
  },
  watch: {
    localItems: {
      deep: true,
      handler() {
        if (!this.syncing) {
          this.emitItems();
        }
      },
    },
    modelValue: {
      deep: true,
      immediate: true,
      handler() {
        this.syncLocalItems();
      },
    },
    restTimers: {
      deep: true,
      immediate: true,
      handler() {
        this.syncLocalItems();
      },
    },
    lastExercisesKey: {
      immediate: true,
      handler() {
        this.loadLastExercises();
      },
    },
  },
});
</script>

<style scoped lang="scss">
.chosen {
    background: #888;
}
</style>
